import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Activity, ArrowRight, MessageSquare, User } from "lucide-react";
import { listBookingActivity } from "@/lib/api";
import { BOOKING_STATUSES, STATUS_BADGE_CLASS } from "@/lib/constants";

/**
 * BookingActivityFeed — vertical timeline of audit-log entries for a booking.
 * Renders status transitions with from→to badges, the actor, and any reason.
 */
export default function BookingActivityFeed({ bookingId }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await listBookingActivity(bookingId);
      if (cancelled) return;
      if (error) {
        // eslint-disable-next-line no-console
        console.warn("activity load failed", error);
        setRows([]);
        return;
      }
      setRows(data || []);
    })();
    return () => { cancelled = true; };
  }, [bookingId]);

  if (rows === null) {
    return (
      <div data-testid="activity-loading" className="text-sm text-slate-500 py-6">
        Loading activity…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div data-testid="activity-empty" className="py-8 text-center text-sm text-slate-500">
        <Activity className="h-6 w-6 mx-auto text-slate-300 mb-2" />
        No activity yet. As soon as anyone changes this booking, the timeline will appear here.
      </div>
    );
  }

  return (
    <ol className="space-y-3" data-testid="activity-feed">
      {rows.map((r) => (
        <ActivityRow key={r.id} row={r} />
      ))}
    </ol>
  );
}

function ActivityRow({ row }) {
  const { fromStatus, toStatus, reason } = parseStatusAction(row);
  const fromMeta = BOOKING_STATUSES.find((s) => s.value === fromStatus);
  const toMeta = BOOKING_STATUSES.find((s) => s.value === toStatus);
  const actor = formatActor(row.user, row.user_id);

  return (
    <li className="relative pl-7" data-testid={`activity-row-${row.id}`}>
      {/* Timeline dot */}
      <span
        className="absolute left-2 top-1.5 h-2.5 w-2.5 rounded-full bg-blue-500 ring-4 ring-blue-500/15"
        aria-hidden
      />
      <div className="text-xs text-slate-500 tabular-nums">{fmtFull(row.created_at)}</div>

      {fromStatus && toStatus ? (
        <div className="flex items-center gap-1.5 mt-1 text-sm flex-wrap">
          {fromMeta && (
            <Badge variant="secondary" className={STATUS_BADGE_CLASS[fromMeta.tone]}>
              {fromMeta.label}
            </Badge>
          )}
          <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
          {toMeta && (
            <Badge variant="secondary" className={STATUS_BADGE_CLASS[toMeta.tone]}>
              {toMeta.label}
            </Badge>
          )}
          <span className="text-slate-400 mx-1">·</span>
          <span className="text-slate-600 inline-flex items-center gap-1 text-xs">
            <User className="h-3 w-3" /> {actor}
          </span>
        </div>
      ) : (
        <div className="text-sm mt-1">
          <span className="font-medium">{row.action}</span>
          <span className="text-slate-400 mx-2">·</span>
          <span className="text-slate-600 inline-flex items-center gap-1 text-xs">
            <User className="h-3 w-3" /> {actor}
          </span>
        </div>
      )}

      {reason && (
        <div className="mt-1 ml-1 inline-flex items-start gap-1 text-xs text-slate-600 bg-slate-50 rounded px-2 py-1">
          <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0" />
          {reason}
        </div>
      )}
    </li>
  );
}

/** "status:draft->pending_approval" → { fromStatus, toStatus, reason }. */
function parseStatusAction(row) {
  const m = /^status:([a-z_]+)->([a-z_]+)$/.exec(row.action || "");
  if (!m) return { fromStatus: null, toStatus: null, reason: row.new_values?.reason || null };
  return {
    fromStatus: m[1],
    toStatus:   m[2],
    reason:     row.new_values?.reason || null,
  };
}

function formatActor(user, userId) {
  if (!user) return userId ? "Unknown user" : "System";
  const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
  return name || user.email || "Unknown user";
}

function fmtFull(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-AU", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}
